/**
 * PreflightPanel — alerta explícito quando preflight tem checks com status='fail'.
 *
 * Gap 3 (2026-05-10): complementa o PreflightChecklist existente com um banner
 * vermelho dedicado listando os checks bloqueantes. Submit permanece desabilitado
 * enquanto qualquer check tenha status='fail' (via can_submit=false no backend).
 *
 * Usa o shape legado {name, status, detail} (PreflightCheckResult) — NÃO o shape
 * blockers/warnings do plano, pois o aggregator backend usa to_legacy_dict().
 *
 * Spec: docs/superpowers/plans/2026-05-10-ztna-resilience-gap3-preflight-expanded.md
 */
import { Alert, Badge, Group, Stack, Text } from '@mantine/core'
import { IconAlertTriangle, IconShieldX } from '@tabler/icons-react'
import type { PreflightCheckResult, PreflightResponse } from '../types'

interface Props {
  result: PreflightResponse | null | undefined
  loading?: boolean
}

function failChecks(checks: PreflightCheckResult[]): PreflightCheckResult[] {
  return checks.filter((c) => c.status === 'fail')
}

function warnChecks(checks: PreflightCheckResult[]): PreflightCheckResult[] {
  return checks.filter((c) => c.status === 'warn')
}

/**
 * Renderiza banners de blockers (status='fail') e avisos (status='warn').
 * Só exibe quando há checks relevantes — sem resultado ainda, renderiza nada.
 */
export function PreflightPanel({ result, loading }: Props) {
  if (loading || !result || !result.checks || result.checks.length === 0) {
    return null
  }

  const blockers = failChecks(result.checks)
  const warnings = warnChecks(result.checks)

  if (blockers.length === 0 && warnings.length === 0) {
    return null
  }

  return (
    <Stack gap="xs" mt="xs">
      {blockers.length > 0 && (
        <Alert
          color="red"
          variant="light"
          icon={<IconShieldX size={16} />}
          title={`${blockers.length} bloqueador(es) — submit desabilitado`}
        >
          <Stack gap={4}>
            {blockers.map((c) => (
              <Group key={c.name} gap={6} wrap="nowrap" align="flex-start">
                <Badge size="xs" color="red" variant="filled" style={{ flexShrink: 0 }}>
                  FAIL
                </Badge>
                <Text size="sm" fw={500} style={{ minWidth: 0 }}>
                  {c.name}
                  {c.detail ? (
                    <Text component="span" size="xs" c="dimmed" ml={4}>
                      — {c.detail}
                    </Text>
                  ) : null}
                </Text>
              </Group>
            ))}
          </Stack>
        </Alert>
      )}
      {warnings.length > 0 && (
        <Alert
          color="yellow"
          variant="light"
          icon={<IconAlertTriangle size={16} />}
          title={`${warnings.length} aviso(s) — não bloqueia submit`}
        >
          <Stack gap={4}>
            {warnings.map((c) => (
              <Group key={c.name} gap={6} wrap="nowrap" align="flex-start">
                <Badge size="xs" color="yellow" variant="filled" style={{ flexShrink: 0 }}>
                  WARN
                </Badge>
                <Text size="sm" style={{ minWidth: 0 }}>
                  {c.name}
                  {c.detail ? (
                    <Text component="span" size="xs" c="dimmed" ml={4}>
                      — {c.detail}
                    </Text>
                  ) : null}
                </Text>
              </Group>
            ))}
          </Stack>
        </Alert>
      )}
    </Stack>
  )
}
